/**
 * QelliPublicNewsRadar.jsx — Phase 12.6 · public, unauthed live news
 * strip used on the Landing page directly under "Meet Qelli".
 *
 * Pulls 3 freshest cyber/crypto/fintech headlines from the public
 * `/qelli/news/radar` endpoint and renders them as a single-row strip
 * that visually anchors the Meet Qelli CTA — "your AI is alive and
 * watching for you" social proof in the public sales funnel.
 *
 * Previously lived inline inside Footer.jsx (Iter275.3). Promoted out
 * so it sits next to the value prop instead of buried below the fold.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Rss } from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function QelliPublicNewsRadar() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/qelli/news/radar`, { params: { limit: 3, hours: 72 } })
         .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
         .catch(() => { /* silent — strip just disappears */ });
    return () => { cancelled = true; };
  }, []);

  if (!items.length) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 sm:px-8 pt-2 pb-12"
             data-testid="landing-qelli-news-radar">
      <div className="flex items-center gap-2 mb-3">
        <span style={{
          display: "inline-block", width: 8, height: 8, borderRadius: 999,
          background: "#1FDD83",
          boxShadow: "0 0 12px rgba(31,221,131,0.85)",
          animation: "qt-pulse 1.6s ease-in-out infinite",
        }} />
        <span className="qt-mono text-[11px]"
              style={{ color: "var(--qt-cyan)",
                       letterSpacing: "0.18em", fontWeight: 700 }}>
          LIVE FROM QELLI · NEWS RADAR
        </span>
        <Link
          to="/threat-radar"
          data-testid="landing-qelli-news-radar-link"
          className="qt-mono text-[10px] ml-2"
          style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em",
                   textDecoration: "underline" }}
        >
          SEE ALL
        </Link>
        {/* Iter273.5 — Beta-tester #35: RSS subscribe link on the
            public landing surface so visitors can subscribe BEFORE
            signing up. */}
        <a
          href={`${API}/qelli/news/radar.rss`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="landing-qelli-news-radar-rss"
          title="Subscribe to Qelli's News Radar via RSS"
          className="qt-mono text-[10px] inline-flex items-center gap-1 ml-1"
          style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em",
                   textDecoration: "none" }}
        >
          <Rss size={9} /> RSS
        </a>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {items.slice(0, 3).map((it, idx) => (
          <a key={it.id || idx}
             href={it.link}
             target="_blank"
             rel="noopener noreferrer"
             data-testid={`landing-qelli-news-radar-item-${idx}`}
             className="qt-card"
             style={{
               padding: "12px 14px",
               border: "1px solid var(--qt-border)",
               background: "rgba(7,10,18,0.45)",
               textDecoration: "none",
               transition: "border-color 0.2s",
             }}
             onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--qt-cyan)")}
             onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--qt-border)")}
          >
            <div className="qt-mono text-[9px] mb-1"
                 style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em" }}>
              {(it.source_label || it.sector || "FEED").toUpperCase()}
            </div>
            <div className="text-sm leading-snug"
                 style={{ color: "var(--qt-text-1)" }}>
              {(it.title || "").slice(0, 110)}
              {(it.title || "").length > 110 ? "…" : ""}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
